<script setup lang="ts">
import { BRAND_NAME, type FeaturesDto } from '@bill-pulse/shared';
import { api } from '@/lib/api';
import { computed, onMounted, ref, watch } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import NavIcon from '@/components/NavIcon.vue';
import BrandMark from '@/components/BrandMark.vue';
import UserActions from '@/components/UserActions.vue';

const auth = useAuthStore();
const route = useRoute();
const router = useRouter();

/** Repli persisté localement : c'est une préférence d'affichage, pas une donnée métier. */
const STORAGE_KEY = 'billpulse.sidebarCollapsed';
const collapsed = ref(false);
const mobileOpen = ref(false);

onMounted(() => {
  try {
    collapsed.value = window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    // Navigation privée ou stockage bloqué : on reste déplié, sans bloquer l'affichage.
  }
});

function toggleCollapsed(): void {
  collapsed.value = !collapsed.value;
  try {
    window.localStorage.setItem(STORAGE_KEY, collapsed.value ? '1' : '0');
  } catch {
    // Tant pis : la préférence ne survivra pas à un rechargement.
  }
}

interface NavItem {
  to: string;
  label: string;
  icon: 'dashboard' | 'invoices' | 'companies' | 'receipts' | 'expenses' | 'quotes' | 'accounting' | 'bank' | 'admin' | 'calendar' | 'phone';
  adminOnly?: boolean;
  /** Fonction activable par l'administration (`/api/me/features`). */
  feature?: keyof FeaturesDto;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/dashboard', label: 'Tableau de bord', icon: 'dashboard' },
  { to: '/invoices', label: 'Factures', icon: 'invoices' },
  { to: '/quotes', label: 'Devis', icon: 'quotes' },
  { to: '/planning', label: 'Planning', icon: 'calendar' },
  { to: '/demandes', label: 'Demandes', icon: 'phone', feature: 'phone' },
  { to: '/signatures', label: 'Signatures', icon: 'quotes', feature: 'signatures' },
  { to: '/receipts', label: 'Recettes', icon: 'receipts' },
  { to: '/expenses', label: 'Dépenses', icon: 'expenses' },
  { to: '/bank', label: 'Banque', icon: 'bank' },
  { to: '/accounting', label: 'Comptabilité', icon: 'accounting' },
  { to: '/clients', label: 'Clients', icon: 'companies' },
  { to: '/admin', label: 'Administration', icon: 'admin', adminOnly: true },
];

const features = ref<FeaturesDto>({ bank: false, phone: false, signatures: false });
onMounted(() => {
  void api.features().then((value) => (features.value = value)).catch(() => undefined);
});

const visibleItems = computed(() =>
  NAV_ITEMS.filter((item) => (!item.adminOnly || auth.isAdmin) && (!item.feature || features.value[item.feature])),
);

async function logout(): Promise<void> {
  // La déconnexion ne doit jamais rester bloquée : même si l'appel réseau
  // échoue, on efface la session locale et on quitte la page.
  try {
    await auth.logout();
  } catch {
    auth.user = null;
  }
  mobileOpen.value = false;
  await router.push({ name: 'login' });
}

// Ferme le tiroir mobile dès qu'on navigue (clic sur un lien, retour arrière…).
watch(
  () => route.fullPath,
  () => {
    mobileOpen.value = false;
  },
);
</script>

<template>
  <!-- Racine unique (`display: contents`) : le composant rend trois blocs de
       niveaux différents (barre mobile, tiroir, sidebar fixe) qui ne doivent
       PAS devenir eux-mêmes des items du flex row du parent (App.vue). -->
  <div class="contents">
  <!-- Barre mobile : logo + bouton menu. `fixed` plutôt que `sticky` — sinon,
       même en `contents`, elle participerait au flux et donc au flex du
       parent, ce qui la comprimerait au lieu de courir toute la largeur. -->
  <header
    class="safe-top fixed inset-x-0 top-0 z-30 flex items-center justify-between border-b border-slate-200 bg-surface/90 px-4 py-3 backdrop-blur sm:hidden"
  >
    <RouterLink to="/invoices" class="flex items-center gap-2 font-bold text-ink">
      <BrandMark />
      {{ BRAND_NAME }}
    </RouterLink>
    <div class="flex items-center gap-2">
      <UserActions compact />
      <button
        type="button"
        class="btn-ghost"
        :aria-expanded="mobileOpen"
        aria-label="Ouvrir le menu"
        @click="mobileOpen = true"
      >
        <NavIcon name="menu" />
      </button>
    </div>
  </header>

  <!-- Tiroir mobile : voile + panneau, au-dessus du contenu. -->
  <div v-if="mobileOpen" class="fixed inset-0 z-40 sm:hidden">
    <div class="absolute inset-0 bg-black/30" @click="mobileOpen = false" />
    <aside class="safe-top safe-bottom relative flex h-full w-64 max-w-[80vw] flex-col bg-surface shadow-xl">
      <div class="px-4 py-4">
        <RouterLink to="/invoices" class="flex items-center gap-2 font-bold text-ink" @click="mobileOpen = false">
          <BrandMark />
          {{ BRAND_NAME }}
        </RouterLink>
      </div>
      <nav class="flex-1 space-y-1 px-2">
        <RouterLink
          v-for="item in visibleItems"
          :key="item.to"
          :to="item.to"
          class="flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-ink-soft transition hover:bg-slate-100"
          active-class="bg-pulse-50 text-pulse-700"
        >
          <NavIcon :name="item.icon" />
          {{ item.label }}
        </RouterLink>
      </nav>
      <div class="border-t border-slate-200 px-2 py-3">
        <button
          type="button"
          class="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-ink-soft transition hover:bg-slate-100"
          @click="logout"
        >
          <NavIcon name="logout" />
          Déconnexion
        </button>
      </div>
    </aside>
  </div>

  <!-- Sidebar fixe desktop, repliable en mode icônes. -->
  <aside
    class="sticky top-0 hidden h-screen shrink-0 flex-col border-r border-slate-200 bg-surface transition-[width] duration-200 sm:flex"
    :class="collapsed ? 'w-[72px]' : 'w-60'"
  >
    <!-- Logo à gauche, bouton de repli (icône seule) en haut à droite de la
         colonne. Replié, le bouton reste seul, centré en tête de colonne. -->
    <div class="flex items-center px-2 py-3" :class="collapsed ? 'justify-center' : 'justify-between pl-4'">
      <RouterLink v-if="!collapsed" to="/invoices" class="flex items-center gap-2 font-bold text-ink">
        <BrandMark />
        {{ BRAND_NAME }}
      </RouterLink>
      <button
        type="button"
        class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-ink-soft transition hover:bg-slate-100"
        :aria-label="collapsed ? 'Déplier le menu' : 'Réduire le menu'"
        :title="collapsed ? 'Déplier le menu' : 'Réduire le menu'"
        :aria-expanded="!collapsed"
        @click="toggleCollapsed"
      >
        <NavIcon name="sidebar" />
      </button>
    </div>

    <nav class="flex-1 space-y-1 px-2">
      <RouterLink
        v-for="item in visibleItems"
        :key="item.to"
        :to="item.to"
        :title="collapsed ? item.label : undefined"
        class="flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-ink-soft transition hover:bg-slate-100"
        active-class="bg-pulse-50 text-pulse-700"
        :class="collapsed && 'justify-center px-0'"
      >
        <NavIcon :name="item.icon" />
        <span v-if="!collapsed">{{ item.label }}</span>
      </RouterLink>
    </nav>

    <div class="border-t border-slate-200 px-2 py-3">
      <button
        type="button"
        :title="collapsed ? 'Déconnexion' : undefined"
        class="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-ink-soft transition hover:bg-slate-100"
        :class="collapsed && 'justify-center px-0'"
        @click="logout"
      >
        <NavIcon name="logout" />
        <span v-if="!collapsed">Déconnexion</span>
      </button>
    </div>
  </aside>
  </div>
</template>
